import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { get } from '@ember/helper';
import { service } from '@ember/service';
import type Intl from 'ember-intl/services/intl';
import { registerDestructor } from '@ember/destroyable';
import type Owner from '@ember/owner';

const defaultIntervalMs = 700;

export interface TioLoadingMessagesSignature {
  Args: {
    /**
     * The root path to all translation keys used by this component.
     */
    i18nPath?: string;

    /**
     * The messages to display. Either this or `@messageKeys` must be provided.
     */
    messages?: string[];

    /**
     * The translation keys for the messages to display. Either this or
     * `@messages` must be provided. If `@i18nPath` is provided, the keys will be
     * scoped to this path.
     */
    messageKeys?: string[];

    /**
     * The number of milliseconds before the first message is displayed.
     * Defaults to `3500`.
     */
    initialIntervalMs?: number;

    /**
     * The number of milliseconds to display each message before switching to the
     * next one.
     */
    messageIntervalMs?: number;
  };
  Element: HTMLElement;
}

/**
 * Displays loading messages in a rotating fashion.
 *
 * ## Usage
 *
 * Display this component conditionally when you want the messages to appear.
 * Messages will start `initialIntervalMs` milliseconds after the component is
 * mounted and will continue until unmounted.
 *
 * If you want to position the messages, wrap them in a container and apply
 * styles to that container.
 *
 * For example:
 *
 * ```
 * {{#if form.submissionState.isPending}}
 *   <div class="mt-6 text-center">
 *     <Tio::LoadingMessages
 *       @i18nPath="login.loading_messages"
 *       @messageKeys={{array
 *         "verify_credentials"
 *         "wait_for_system_warmup"
 *         "thank_you_for_your_patience"
 *         "gather_account_details"
 *         "fetch_company_data"
 *       }}
 *     />
 *   </div>
 * {{/if}}
 * ```
 */
export default class TioLoadingMessagesComponent extends Component<TioLoadingMessagesSignature> {
  @service declare intl: Intl;

  timerId?: ReturnType<typeof setTimeout>;

  @tracked index = -1;

  constructor(owner: Owner, args: TioLoadingMessagesSignature['Args']) {
    super(owner, args);

    registerDestructor(this, () => {
      this.stop();
    });

    this.start();
  }

  get messages() {
    const { messageKeys = [], messages, i18nPath } = this.args;

    return messages
      ? messages
      : messageKeys.map((key: string) => this.intl.t(i18nPath ? `${i18nPath}.${key}` : key));
  }

  get showingMessages() {
    return this.index >= 0;
  }

  rotateMessage() {
    this.index = (this.index + 1) % this.messages.length;

    this.timerId = setTimeout(
      () => this.rotateMessage(),
      this.args.messageIntervalMs ?? defaultIntervalMs
    );
  }

  start = () => {
    this.timerId = setTimeout(
      () => this.rotateMessage(),
      this.args.initialIntervalMs ?? defaultIntervalMs
    );
  };

  stop = () => {
    clearTimeout(this.timerId);
  };

  <template>
    <div>
      {{get this.messages this.index}}
    </div>
  </template>
}
